import { useEffect, useRef, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CodigoRealInput } from "@/components/CodigoRealInput";
import { Sparkles, X, AlertTriangle, CircleCheck, Ban, RotateCcw, GripVertical, CornerUpRight, Plus } from "lucide-react";

const BADGE = {
  ok: { txt: "OK", cls: "bg-teal-100 text-teal-700", Icon: CircleCheck },
  ambiguo: { txt: "AMBIGUO", cls: "bg-amber-100 text-amber-800", Icon: AlertTriangle },
  faltante: { txt: "FALTANTE", cls: "bg-pink-100 text-pink-700", Icon: Ban },
};

export const GenerarPlantillasIADialog = ({ maestraId, maestraNombre, modelosObjetivo, open, onClose, onDone }) => {
  const [cargando, setCargando] = useState(false);
  const [propuestas, setPropuestas] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [vacio, setVacio] = useState(false);
  const [manoObraHeredada, setManoObraHeredada] = useState(0);
  const [frtHeredada, setFrtHeredada] = useState(0);
  const revalTimers = useRef({});

  useEffect(() => {
    if (!open || !maestraId) return;
    setPropuestas([]); setVacio(false); setCargando(true);
    const params = (modelosObjetivo && modelosObjetivo.length) ? { modelos: modelosObjetivo.join(",") } : {};
    api.post(`/plantillas/${maestraId}/generar-ia`, null, { params })
      .then(({ data }) => {
        setPropuestas(data.propuestas || []); setVacio((data.propuestas || []).length === 0);
        setManoObraHeredada(Number(data.mano_obra_heredada) || 0);
        setFrtHeredada(Number(data.frt_heredada) || 0);
      })
      .catch((e) => toast.error(errorMsg(e)))
      .finally(() => setCargando(false));
  }, [open, maestraId, modelosObjetivo]);

  const setProp = (pi, patch) => setPropuestas((prev) => prev.map((p, i) => (i === pi ? { ...p, ...patch } : p)));
  const setItem = (pi, ii, patch) => setPropuestas((prev) => prev.map((p, i) =>
    i !== pi ? p : { ...p, items: p.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) }));
  const quitarItem = (pi, ii) => setPropuestas((prev) => prev.map((p, i) =>
    i !== pi ? p : { ...p, items: p.items.filter((_, j) => j !== ii) }));
  const quitarProp = (pi) => setPropuestas((prev) => prev.filter((_, i) => i !== pi));

  // Excluye un modelo de ESTA generación (no toca el sistema ni la maestra). Si la tarjeta
  // queda sin modelos, se descarta para no crear una plantilla vacía.
  const quitarModelo = (pi, modeloId) => setPropuestas((prev) => {
    const next = prev.map((p, i) => i !== pi ? p : {
      ...p,
      modelo_ids: (p.modelo_ids || []).filter((id) => id !== modeloId),
      modelos: (p.modelos || []).filter((m) => m.id !== modeloId),
    });
    return next.filter((p) => (p.modelos && p.modelos.length > 0));
  });

  // Agrega una línea nueva vacía a la tarjeta (código/nombre editables, cantidad manual).
  const agregarItem = (pi) => setPropuestas((prev) => prev.map((p, i) => i !== pi ? p : {
    ...p, items: [...p.items, {
      prefijo: null, nombre_maestra: "", nombre_catalogo: "", codigo: "", repuesto_id: null,
      crear_repuesto: false, estado: "faltante", cantidad: 1, cantidad_manual: true,
      opcional: false, fijo: false, opciones: [], manual_add: true,
    }],
  }));

  const moverModelo = (fromPi, toPi, modeloId) => {
    if (fromPi === toPi) return;
    setPropuestas((prev) => {
      const modelo = (prev[fromPi]?.modelos || []).find((m) => m.id === modeloId);
      if (!modelo) return prev;
      let next = prev.map((p, i) => {
        if (i === fromPi) return { ...p, modelo_ids: (p.modelo_ids || []).filter((id) => id !== modeloId),
          modelos: (p.modelos || []).filter((m) => m.id !== modeloId) };
        if (i === toPi) {
          if ((p.modelo_ids || []).includes(modeloId)) return p;
          return { ...p, modelo_ids: [...(p.modelo_ids || []), modeloId], modelos: [...(p.modelos || []), modelo] };
        }
        return p;
      });
      next = next.filter((p) => (p.modelos && p.modelos.length > 0));
      return next;
    });
  };

  const nombreDe = (it) => it.nombre_catalogo ?? it.nombre_maestra ?? "";
  const incluible = (it) => !!it.repuesto_id ||
    (!!it.crear_repuesto && (!!String(it.codigo || "").trim() || !!String(nombreDe(it)).trim()));

  const aplicarCodigo = (pi, ii, it, val) => {
    const opt = (it.opciones || []).find((o) => o.codigo_parte === val);
    const nombre = opt ? opt.nombre : nombreDe(it);
    const activo = !!(String(val).trim() || String(nombre).trim());
    setItem(pi, ii, {
      codigo: val,
      ...(opt ? { nombre_catalogo: opt.nombre } : {}),
      repuesto_id: null,
      crear_repuesto: activo,
      estado: activo ? "ok" : "faltante",
      sugerido_ia: false,
    });
    // Revalidar contra repuestos: si el código existe, enlazar como (existe) sin duplicar.
    const key = `${pi}-${ii}`;
    if (revalTimers.current[key]) clearTimeout(revalTimers.current[key]);
    const code = String(val || "").trim();
    if (!code) return;
    revalTimers.current[key] = setTimeout(async () => {
      try {
        const { data } = await api.get("/repuestos/buscar-por-codigo", { params: { codigo: code } });
        if (data && data.id) {
          setPropuestas((prev) => prev.map((p, i) => i !== pi ? p : {
            ...p, items: p.items.map((x, j) => (j === ii && String(x.codigo || "").trim().toLowerCase() === code.toLowerCase())
              ? { ...x, repuesto_id: data.id, codigo: data.codigo_parte, nombre_catalogo: data.nombre, estado: "ok", crear_repuesto: false, sugerido_ia: false }
              : x) }));
        }
      } catch { /* ignore */ }
    }, 350);
  };

  const aplicarNombre = (pi, ii, it, val) => {
    const activo = !!(String(it.codigo || "").trim() || String(val).trim());
    setItem(pi, ii, {
      nombre_catalogo: val,
      crear_repuesto: it.repuesto_id ? false : activo,
      estado: it.repuesto_id ? "ok" : (activo ? "ok" : "faltante"),
    });
  };

  const esDescartada = (p) => !p.existente_id && !p.hereda && p.items.filter(incluible).length === 0;

  const equivNote = (it) => {
    if (!it.codigo || !it.prefijo) return null;
    const found = String(it.codigo).split("-")[0].toUpperCase();
    return found !== String(it.prefijo).toUpperCase() ? { maestra: it.prefijo, encontrado: found } : null;
  };

  const badgeKey = (it) => {
    if (it.fijo) return "ok";
    if (it.estado === "ambiguo") return "ambiguo";
    if (it.repuesto_id) return "ok";
    return incluible(it) ? "ok" : "faltante";
  };

  const confirmar = async () => {
    setGuardando(true);
    try {
      const payload = {
        maestra_id: maestraId,
        propuestas: (propuestas || []).map((p) => ({
          nombre: p.nombre_sugerido, modelo_ids: p.modelo_ids || [], mano_de_obra: p.mano_de_obra || 0,
          frt: p.frt ?? null, existente_id: p.existente_id || null, hereda: !!p.hereda,
          items: (p.items || []).filter(incluible).map((it) => ({
            codigo: it.codigo || "", nombre: nombreDe(it), cantidad: it.cantidad || 1,
            repuesto_id: it.repuesto_id || null, crear_repuesto: !!it.crear_repuesto, categoria: "",
            opcional: !!it.opcional, fijo: !!it.fijo, prefijo: it.prefijo || null,
            origen_herencia: it.origen_herencia || null })),
        })).filter((p) => p.items.length > 0 || p.existente_id || p.hereda),
      };
      if (payload.propuestas.length === 0) { toast.error("No hay plantillas válidas para crear"); return; }
      const { data } = await api.post("/plantillas/generar-ia/confirmar", payload);
      toast.success(`Creadas: ${data.creadas} · Actualizadas: ${data.actualizadas} · Repuestos nuevos: ${data.repuestos_creados}`);
      onDone(); onClose();
    } catch (e) {
      console.error("Error al crear plantillas:", e);
      toast.error(errorMsg(e) || "No se pudieron crear las plantillas");
    } finally { setGuardando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="generar-ia-dialog" className="bg-white max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={18} className="text-indigo-600" /> Generar plantillas con IA — {maestraNombre}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">Revise, edite y confirme. Nada se crea hasta confirmar. Puede arrastrar un modelo de una tarjeta a otra para fusionarlas.</p>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
          {cargando && <p data-testid="generar-ia-cargando" className="py-10 text-center text-sm text-muted-foreground">Analizando el catálogo con IA…</p>}
          {vacio && !cargando && <p data-testid="generar-ia-vacio" className="py-10 text-center text-sm text-muted-foreground">No hay otros modelos con catálogo de referencia para replicar.</p>}

          {propuestas.map((p, pi) => {
            const descartada = esDescartada(p);
            const faltantesModelo = [...new Set((p.items || [])
              .filter((it) => !it.fijo && !it.repuesto_id && it.estado === "faltante")
              .map((it) => (it.nombre_maestra || it.prefijo || "").trim()).filter(Boolean))];
            return (
            <div key={pi} data-testid={`propuesta-${pi}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                try {
                  const d = JSON.parse(e.dataTransfer.getData("text/plain"));
                  if (d && typeof d.fromPi === "number" && d.modeloId) moverModelo(d.fromPi, pi, d.modeloId);
                } catch { /* payload inválido */ }
              }}
              className={`rounded-sm border p-3 ${descartada ? "border-pink-200 bg-pink-50/40" : "border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <Input data-testid={`propuesta-nombre-${pi}`} value={p.nombre_sugerido}
                  onChange={(e) => setProp(pi, { nombre_sugerido: e.target.value })} className="font-semibold" />
                <button data-testid={`propuesta-quitar-${pi}`} onClick={() => quitarProp(pi)}
                  className="text-slate-400 hover:text-pink-600 shrink-0"><X size={16} /></button>
              </div>
              {descartada && (
                <div data-testid={`propuesta-descartada-${pi}`} className="flex items-center gap-1 mb-2 text-[11px] text-pink-700 bg-pink-100 px-2 py-1 rounded-sm font-medium">
                  <Ban size={12} /> Se descartará al confirmar · sin repuestos válidos
                </div>
              )}
              {!descartada && p.hereda && p.items.filter(incluible).length === 0 && (
                <div data-testid={`propuesta-hereda-${pi}`} className="flex items-center gap-1 mb-2 text-[11px] text-amber-800 bg-amber-100 px-2 py-1 rounded-sm font-medium">
                  <Sparkles size={12} /> Sin repuestos propios a resolver — se hereda de la plantilla padre (mano de obra, FRT y repuestos heredados)
                </div>
              )}
              {!descartada && faltantesModelo.length > 0 && (
                <div data-testid={`propuesta-faltantes-${pi}`} className="flex items-start gap-1 mb-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded-sm font-medium">
                  <Ban size={12} className="mt-0.5 shrink-0" />
                  <span>{p.modelos?.length > 1 ? "Estos modelos no tienen" : "Este modelo no tiene"} en catálogo: <b>{faltantesModelo.join(", ")}</b> — {p.modelos?.length > 1 ? "quedarán" : "quedará"} sin ese repuesto.</span>
                </div>
              )}
              <div className="flex flex-wrap gap-1 mb-2 items-center">
                {p.modelos.map((m) => (
                  <span key={m.id} draggable data-testid={`propuesta-${pi}-modelo-${m.id}`}
                    onDragStart={(e) => e.dataTransfer.setData("text/plain", JSON.stringify({ fromPi: pi, modeloId: m.id }))}
                    className="group inline-flex items-center gap-1 text-[10px] bg-accent text-accent-foreground pl-1 pr-0.5 py-0.5 rounded-sm font-medium cursor-grab active:cursor-grabbing">
                    <GripVertical size={10} className="opacity-40" />
                    {m.nombre_mostrar}
                    {propuestas.filter((_, ti) => ti !== pi).length > 0 && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`propuesta-${pi}-modelo-${m.id}-mover`} title="Mover a…"
                            className="ml-0.5 rounded-sm hover:bg-black/10 p-0.5"><CornerUpRight size={11} /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="bg-white">
                          {propuestas.map((tp, ti) => (ti === pi ? null : (
                            <DropdownMenuItem key={ti} data-testid={`mover-${pi}-${m.id}-a-${ti}`}
                              onClick={() => moverModelo(pi, ti, m.id)} className="text-xs">
                              {tp.nombre_sugerido}
                            </DropdownMenuItem>
                          )))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    <button data-testid={`propuesta-${pi}-modelo-${m.id}-quitar`} title="Excluir de esta generación"
                      onClick={() => quitarModelo(pi, m.id)}
                      className="ml-0.5 rounded-sm hover:bg-pink-200/60 hover:text-pink-700 p-0.5"><X size={11} /></button>
                  </span>
                ))}
                {p.existente_id && (
                  <span data-testid={`propuesta-existente-${pi}`} className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-sm font-medium">
                    Se agregará el/los modelo(s) a una plantilla existente equivalente
                  </span>
                )}
              </div>
              <div className="flex gap-3 mb-2 items-center flex-wrap">
                <label className="text-xs text-muted-foreground flex items-center gap-1">Mano de obra ₡
                  <Input type="number" className="h-7 w-24" value={p.mano_de_obra}
                    onChange={(e) => setProp(pi, { mano_de_obra: Number(e.target.value) || 0 })} /></label>
                <label className="text-xs text-muted-foreground flex items-center gap-1">FRT
                  <Input type="number" className="h-7 w-20" value={p.frt ?? ""}
                    onChange={(e) => setProp(pi, { frt: e.target.value === "" ? null : Number(e.target.value) })} /></label>
                {(manoObraHeredada > 0 || frtHeredada > 0) && (
                  <span data-testid={`propuesta-efectiva-${pi}`} className="text-[11px] text-teal-700 bg-teal-50 border border-teal-200 px-2 py-1 rounded-sm">
                    Efectiva (con herencia): <b>₡{((Number(p.mano_de_obra) || 0) + manoObraHeredada).toLocaleString("es-CR")}</b>
                    {frtHeredada > 0 && <> · FRT <b>{(Math.round(((Number(p.frt) || 0) + frtHeredada) * 10) / 10)}</b></>}
                  </span>
                )}
              </div>
              <div className="overflow-x-auto -mx-1 px-1">
              <table className="w-full min-w-[520px] text-xs">
                <thead><tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-1 pr-2">Estado</th><th className="py-1 pr-2">Código real</th>
                  <th className="py-1 pr-2">Repuesto</th><th className="py-1 pr-2 text-right">Cant.</th>
                  <th className="py-1 pr-2">Crear</th><th className="py-1"></th></tr></thead>
                <tbody>
                  {p.items.map((it, ii) => {
                    const b = BADGE[badgeKey(it)] || BADGE.ok;
                    return (
                      <tr key={ii} data-testid={`propuesta-${pi}-item-${ii}`} className="border-b border-border/50 align-top">
                        <td className="py-1 pr-2">
                          <span className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-sm uppercase font-semibold ${b.cls}`}>
                            <b.Icon size={10} />{b.txt}
                          </span>
                          {it.fijo && <span data-testid={`propuesta-${pi}-item-${ii}-fijo`}
                            className="ml-1 text-[9px] bg-indigo-200 text-indigo-800 px-1 py-0.5 rounded-sm uppercase font-semibold">Fijo</span>}
                          {it.sugerido_ia && <span className="ml-1 text-[9px] text-indigo-600 font-semibold">IA</span>}
                        </td>
                        <td className="py-1 pr-2 font-mono">
                          {it.fijo ? (
                            <span data-testid={`propuesta-${pi}-item-${ii}-codigo`} className="text-xs">{it.codigo}</span>
                          ) : (
                          <><CodigoRealInput testId={`propuesta-${pi}-item-${ii}-codigo`}
                            value={it.codigo || ""} placeholder={it.prefijo ? `prefijo ${it.prefijo}` : "código o buscar…"}
                            onType={(val) => aplicarCodigo(pi, ii, it, val)}
                            onPick={(r) => setItem(pi, ii, { codigo: r.codigo_parte, nombre_catalogo: r.nombre,
                              repuesto_id: r.id, estado: "ok", crear_repuesto: false, sugerido_ia: false })} />
                          {(() => { const eq = equivNote(it); return eq ? (
                            <span data-testid={`propuesta-${pi}-item-${ii}-equiv`} title="Coincidencia por prefijo equivalente"
                              className="mt-0.5 block text-[9px] text-purple-700 bg-purple-100 px-1 py-0.5 rounded-sm w-fit font-sans">
                              por equivalencia {eq.maestra}↔{eq.encontrado}
                            </span>) : null; })()}</>
                          )}
                        </td>
                        <td className="py-1 pr-2">
                          {it.repuesto_id ? (
                            <span className="flex items-center gap-1">
                              {nombreDe(it)}<span className="text-[9px] text-teal-600">(existe)</span>
                            </span>
                          ) : (
                            <Input data-testid={`propuesta-${pi}-item-${ii}-nombre`}
                              value={nombreDe(it)}
                              onChange={(e) => aplicarNombre(pi, ii, it, e.target.value)}
                              className="h-6 text-xs" placeholder="Nombre del repuesto" />
                          )}
                        </td>
                        <td className="py-1 pr-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {it.cantidad_manual ? (
                              <Input type="number" min="0" step="0.01" className="h-6 w-20 text-right px-2 no-spinner" value={it.cantidad}
                                data-testid={`propuesta-${pi}-item-${ii}-cant`}
                                onChange={(e) => { const v = parseFloat(String(e.target.value).replace(",", ".")); setItem(pi, ii, { cantidad: v > 0 ? v : 1 }); }} />
                            ) : (
                              <span data-testid={`propuesta-${pi}-item-${ii}-cant-auto`} className="whitespace-nowrap">
                                {it.cantidad} <span className="text-[9px] text-muted-foreground">auto</span>
                              </span>
                            )}
                            {!it.fijo && (
                              <label title="Fijar la cantidad a mano (si no, se toma del catálogo)" className="cursor-pointer flex items-center">
                                <Checkbox data-testid={`propuesta-${pi}-item-${ii}-cant-manual`} checked={!!it.cantidad_manual}
                                  onCheckedChange={(v) => setItem(pi, ii, { cantidad_manual: !!v })} />
                              </label>
                            )}
                          </div>
                        </td>
                        <td className="py-1 pr-2">
                          {it.repuesto_id ? <span className="text-[9px] text-teal-600">existe</span>
                            : (String(it.codigo || "").trim() || String(nombreDe(it)).trim()) ? (
                              <Checkbox data-testid={`propuesta-${pi}-item-${ii}-crear`} checked={!!it.crear_repuesto}
                                onCheckedChange={(v) => setItem(pi, ii, { crear_repuesto: !!v })} />
                            ) : "—"}
                        </td>
                        <td className="py-1 text-right">
                          <button data-testid={`propuesta-${pi}-item-${ii}-quitar`} onClick={() => quitarItem(pi, ii)}
                            className="text-slate-400 hover:text-pink-600"><X size={13} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
              <button type="button" data-testid={`propuesta-${pi}-agregar-item`}
                onClick={() => agregarItem(pi)}
                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700">
                <Plus size={13} /> Agregar repuesto
              </button>
            </div>
          );})}
        </div>

        <DialogFooter className="shrink-0 gap-2">
          <Button variant="outline" data-testid="generar-ia-cancelar" onClick={onClose}>Cancelar</Button>
          <Button data-testid="generar-ia-confirmar" onClick={confirmar}
            disabled={guardando || cargando || propuestas.length === 0}
            className="bg-indigo-600 hover:bg-indigo-700 text-white">
            {guardando ? "Creando…" : "Confirmar y crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const InstruccionesIADialog = ({ open, onClose }) => {
  const [txt, setTxt] = useState("");
  const [base, setBase] = useState("");
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCargando(true);
    api.get("/plantillas-ia/config")
      .then(({ data }) => { setTxt(data.instrucciones || ""); setBase(data.instrucciones_base || ""); })
      .catch(() => {}).finally(() => setCargando(false));
  }, [open]);

  const restaurar = () => {
    setTxt(base);
    toast.info("Reglas base restauradas. Pulse Guardar para conservarlas.");
  };

  const guardar = async () => {
    try { await api.put("/plantillas-ia/config", { instrucciones: txt }); toast.success("Instrucciones guardadas"); onClose(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="instrucciones-ia-dialog" className="bg-white max-w-2xl">
        <DialogHeader>
          <DialogTitle>Instrucciones para la IA</DialogTitle>
          <p className="text-xs text-muted-foreground">La IA usa este conocimiento al generar plantillas. Editable.</p>
        </DialogHeader>
        <textarea data-testid="instrucciones-ia-textarea" value={txt} onChange={(e) => setTxt(e.target.value)}
          disabled={cargando} rows={12}
          className="w-full text-sm border border-border rounded-sm p-2 font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-indigo-400" />
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" data-testid="instrucciones-ia-restaurar" onClick={restaurar}
            disabled={cargando || !base} className="text-indigo-600 hover:text-indigo-700 gap-1">
            <RotateCcw size={14} /> Restaurar reglas base
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="instrucciones-ia-cancelar" onClick={onClose}>Cancelar</Button>
            <Button data-testid="instrucciones-ia-guardar" onClick={guardar} className="bg-indigo-600 hover:bg-indigo-700 text-white">Guardar</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
